import {
	createEquipmentArea,
	deleteEquipmentArea,
	updateEquipmentArea,
} from "@/services/organization-service";
import {
	Button,
	Form,
	Input,
	Modal,
	Popconfirm,
	Space,
	Switch,
	Table,
	Tag,
	message,
} from "antd";
import type { FC } from "react";
import { useState } from "react";

interface EquipmentArea {
	id: number;
	name: string;
	createdAt?: string;
	isDeprecated?: boolean;
}

interface EquipmentAreaModalProps {
	visible: boolean;
	onClose: () => void;
	onSuccess: () => void;
	currentEquipmentAreas: EquipmentArea[];
}

export const EquipmentAreaModal: FC<EquipmentAreaModalProps> = ({
	visible,
	onClose,
	onSuccess,
	currentEquipmentAreas,
}) => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [editingId, setEditingId] = useState<number | null>(null);
	const [editForm] = Form.useForm();
	const [pageSize, setPageSize] = useState(5);

	const handleSubmit = async (values: any) => {
		try {
			setLoading(true);
			await createEquipmentArea(values.name.trim());

			message.success("Equipment area created successfully");
			form.resetFields();
			onSuccess();
		} catch (error: any) {
			message.error(error.message || "Failed to create equipment area");
		} finally {
			setLoading(false);
		}
	};

	const handleEdit = (area: EquipmentArea) => {
		setEditingId(area.id);
		editForm.setFieldsValue({
			name: area.name,
			isDeprecated: area.isDeprecated || false,
		});
	};

	const handleUpdate = async (areaId: number, values: any) => {
		try {
			setLoading(true);
			await updateEquipmentArea(areaId, {
				name: values.name.trim(),
				isDeprecated: values.isDeprecated,
			});

			message.success("Equipment area updated successfully");
			setEditingId(null);
			editForm.resetFields();
			onSuccess();
		} catch (error: any) {
			message.error(error.message || "Failed to update equipment area");
		} finally {
			setLoading(false);
		}
	};

	const handleDelete = async (areaId: number) => {
		try {
			await deleteEquipmentArea(areaId);
			message.success("Equipment area deleted successfully");
			onSuccess();
		} catch (error: any) {
			message.error(error.message || "Failed to delete equipment area");
		}
	};

	const handleCancel = () => {
		form.resetFields();
		editForm.resetFields();
		setEditingId(null);
		onClose();
	};

	const equipmentAreaColumns = [
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
			render: (text: string, record: EquipmentArea) => {
				if (editingId === record.id) {
					return (
						<Form
							form={editForm}
							layout="inline"
							onFinish={(values) => handleUpdate(record.id, values)}
							style={{ width: "100%" }}
						>
							<Form.Item
								name="name"
								rules={[
									{ required: true, message: "Name is required" },
									{ min: 1, message: "Name must be at least 1 character" },
								]}
								style={{ flex: 1, marginRight: 8 }}
							>
								<Input />
							</Form.Item>
							<Form.Item
								name="isDeprecated"
								valuePropName="checked"
								style={{ marginRight: 8 }}
							>
								<Switch />
							</Form.Item>
							<Form.Item>
								<Space>
									<Button
										type="primary"
										htmlType="submit"
										size="small"
										loading={loading}
									>
										Save
									</Button>
									<Button
										size="small"
										onClick={() => {
											setEditingId(null);
											editForm.resetFields();
										}}
									>
										Cancel
									</Button>
								</Space>
							</Form.Item>
						</Form>
					);
				}
				return <span>{text}</span>;
			},
		},
		{
			title: "Status",
			key: "status",
			render: (_: any, record: EquipmentArea) => {
				if (editingId === record.id) {
					return null; // Status is shown in edit form
				}
				return (
					<Space>
						{record.isDeprecated && <Tag color="red">Deprecated</Tag>}
						{!record.isDeprecated && <Tag color="green">Active</Tag>}
					</Space>
				);
			},
		},
		{
			title: "Created",
			dataIndex: "createdAt",
			key: "createdAt",
			render: (value: string) => {
				if (!value) return "-";
				try {
					return new Date(value).toLocaleDateString();
				} catch {
					return value;
				}
			},
		},
		{
			title: "Actions",
			key: "actions",
			render: (_: any, record: EquipmentArea) => {
				if (editingId === record.id) {
					return null; // Actions are shown in edit form
				}
				return (
					<Space>
						<Button type="link" onClick={() => handleEdit(record)}>
							Edit
						</Button>
						<Popconfirm
							title="Are you sure you want to delete this equipment area?"
							description="This action cannot be undone. Make sure no assets are using this equipment area."
							onConfirm={() => handleDelete(record.id)}
							okText="Yes"
							cancelText="No"
						>
							<Button type="link" danger>
								Delete
							</Button>
						</Popconfirm>
					</Space>
				);
			},
		},
	];

	return (
		<Modal
			title="Manage Equipment Areas (Asset Locations)"
			open={visible}
			onCancel={handleCancel}
			footer={[
				<Button key="cancel" onClick={handleCancel}>
					Close
				</Button>,
			]}
			width={1000}
		>
			<div className="space-y-6">
				{/* Current Equipment Areas Table */}
				<div>
					<h3 className="text-lg font-medium mb-4">Current Equipment Areas</h3>
					<Table
						columns={equipmentAreaColumns}
						dataSource={currentEquipmentAreas}
						rowKey="id"
						pagination={{
							pageSize: pageSize,
							showSizeChanger: true,
							pageSizeOptions: ["5", "10", "20", "50"],
							onShowSizeChange: (current, size) => {
								setPageSize(size);
							},
							showQuickJumper: true,
							showTotal: (total, range) =>
								`${range[0]}-${range[1]} of ${total} equipment areas`,
						}}
						scroll={{ x: 800 }}
					/>
				</div>

				{/* Add New Equipment Area Form */}
				<div className="border-t pt-6">
					<h3 className="text-lg font-medium mb-4">Add New Equipment Area</h3>
					<Form form={form} layout="vertical" onFinish={handleSubmit}>
						<Form.Item
							name="name"
							label="Equipment Area Name"
							rules={[
								{ required: true, message: "Equipment area name is required" },
								{ min: 1, message: "Name must be at least 1 character" },
							]}
						>
							<Input placeholder="Enter equipment area name" />
						</Form.Item>

						<div className="flex justify-end space-x-2 mt-4">
							<Button onClick={() => form.resetFields()}>Reset</Button>
							<Button type="primary" htmlType="submit" loading={loading}>
								Add Equipment Area
							</Button>
						</div>
					</Form>
				</div>
			</div>
		</Modal>
	);
};
